import { Component, ViewChild } from '@angular/core';
import { DropdownModule } from 'primeng/dropdown';
import { AuthManager } from '../shared/auth/AuthManager';
import {
  AppPractice,
  AppUser,
  DimBlockoffType,
  DoctorScheduleClient,
  EmployeeSchedule,
  PracticeSchedule,
  SaveBlockoffPayload,
  SaveScheduleDayPayload,
  ScheduleBlockoff,
  ScheduleDay,
} from '../shared/services/ppv.api';
import { format, add, startOfWeek, parse, isValid, max, min } from 'date-fns';
import { MenuItem } from 'primeng/api';

type ScheduleDayUI = ScheduleDay & {
  employeeNumber: string;
  startTimeDate: Date;
  endTimeDate: Date;
  firstApptDate: Date;
  lastApptDate: Date;
};

type ScheduleBlockoffUI = ScheduleBlockoff & {
  startTimeDate: Date;
  endTimeDate: Date;
};

@Component({
  selector: 'vc-doctor-schedule',
  templateUrl: './doctor-schedule.component.html',
  styleUrls: ['./doctor-schedule.component.scss'],
  standalone: false
})
export class DoctorScheduleComponent {
  public loading = true;
  public currentUser: AppUser;
  public selectedPractice: AppPractice;
  public schedules: PracticeSchedule[];
  public tabs: MenuItem[];
  public selectedTab: MenuItem;
  public activeTabIndex: number = 0;
  public selectedWeekStartDate: Date;
  public rows: EmployeeSchedule[] = [];
  public weekdays = [0, 1, 2, 3, 4, 5, 6];
  public scheduleDialogShown = false;
  public selectedSchedule: ScheduleDayUI = {} as ScheduleDayUI;
  public blockoffDialogShown = false;
  public selectedBlockoff: ScheduleBlockoffUI = {} as ScheduleBlockoffUI;
  public practices: AppPractice[];
  public scheduleLoading: boolean = false;
  public blockoffTypes: DimBlockoffType[];
  public cloneMenuItems: MenuItem[];
  public working: boolean = false;

  public copyTo = [];
  selectedWeek: PracticeSchedule;

  constructor(
    private authManager: AuthManager,
    private doctorScheduleClient: DoctorScheduleClient
  ) {}

  ngOnInit() {
    this.loadAuth();
  }

  getCloneMenuItems() {
    return this.schedules.map((s, idx) => {
      return {
        label: 'Clone Week ' + s.weekNumber,
        command: () => {
          this.addWeek(s.weekNumber);
        },
      };
    });
  }

  practiceSelected(evt) {
    if (evt.value != null) {
      this.loadSchedule(0);
    }
  }

  openModal(doctor: EmployeeSchedule, dayid: number) {
    this.copyTo = [dayid];
    this.selectedSchedule = this.createModel(doctor, dayid);
    this.scheduleDialogShown = true;
  }

  openBlockoffModal(
    doctor: EmployeeSchedule,
    dayid: number,
    blockoff: ScheduleBlockoff
  ) {
    this.copyTo = [dayid];
    this.selectedSchedule = this.createModel(doctor, dayid);
    this.selectedBlockoff = this.createBlockoffModel(doctor, dayid, blockoff);
    this.blockoffDialogShown = true;
  }

  createBlockoffModel(
    doctor: EmployeeSchedule,
    dayid: number,
    blockoff: ScheduleBlockoff
  ) {
    var day = doctor.weekDays[dayid];
    var retval = {
      ...blockoff,
      startTimeDate: this.timeSpanStringToDate(
        blockoff?.startTime ?? '12:00:00'
      ),
      endTimeDate: this.timeSpanStringToDate(blockoff?.endTime ?? '12:00:00'),
    };
    return retval;
  }

  deleteBlockoff() {
    this.doctorScheduleClient
      .deleteBlockoff(this.selectedBlockoff.scheduleBlockoffId)
      .subscribe((r) => {
        this.loadSchedule();
        this.blockoffDialogShown = false;
      });
  }

  deleteSchedule() {
    this.doctorScheduleClient
      .deleteScheduleDay(this.selectedSchedule.practiceScheduleDayId)
      .subscribe((r) => {
        this.loadSchedule();
        this.scheduleDialogShown = false;
      });
  }

  saveBlockoff() {
    var payload = {
      copyToDays: this.copyTo,
      scheduleId: this.selectedWeek.id,
      scheduleDayId: this.selectedSchedule.practiceScheduleDayId,
      weekday: this.selectedSchedule.weekday,
      scheduleBlockoff: {
        ...this.selectedBlockoff,
        startTime: this.selectedBlockoff.startTimeDate
          .toTimeString()
          .substring(0, 8),
        endTime: this.selectedBlockoff.endTimeDate
          .toTimeString()
          .substring(0, 8),
        reason: this.selectedBlockoff.reason ?? '',
      } as ScheduleBlockoff,
    };

    this.doctorScheduleClient.saveBlockoff(payload).subscribe((r) => {
      this.loadSchedule();
      this.blockoffDialogShown = false;
    });
  }

  saveSchedule() {
    var payload = {
      copyToDays: this.copyTo,
      scheduleId: this.selectedWeek.id,
      employeeNumber: this.selectedSchedule.employeeNumber,
      scheduleDay: {
        ...this.selectedSchedule,
        startTime: this.selectedSchedule.startTimeDate
          .toTimeString()
          .substring(0, 8),
        endTime: this.selectedSchedule.endTimeDate
          .toTimeString()
          .substring(0, 8),
        firstAppt: this.selectedSchedule.firstApptDate
          .toTimeString()
          .substring(0, 8),
        lastAppt: this.selectedSchedule.lastApptDate
          .toTimeString()
          .substring(0, 8),
      } as ScheduleDay,
    };
    this.doctorScheduleClient.saveScheduleDay(payload).subscribe((r) => {
      this.loadSchedule();
      this.scheduleDialogShown = false;
    });
  }

  createModel(doctor: EmployeeSchedule, dayid: number) {
    var day = doctor.weekDays[dayid];
    var retval = {
      ...doctor.weekDays[dayid],
      startTimeDate: this.timeSpanStringToDate(day.startTime),
      endTimeDate: this.timeSpanStringToDate(day.endTime),
      firstApptDate: this.timeSpanStringToDate(day.firstAppt),
      lastApptDate: this.timeSpanStringToDate(day.lastAppt),
      employeeNumber: doctor.employeeNumber,
    };
    return retval;
  }

  timeFormats = ['hh:mm a', 'hh:mma', 'hha', 'hh a'];

  onTimeBlur($event, model, propName: string) {
    let timeString = $event.target.value;

    for (const timeFormat of this.timeFormats) {
      let parsed = parse(timeString, timeFormat, new Date('1900-01-01'));
      if (isValid(parsed)) {
        model[propName] = parsed;
        break;
      }
    }
  }

  onNotAssignedChange(isEmployeeNotAssigned, employeeNumber) {
    this.doctorScheduleClient
      .setEmployeeNotAssigned({
        employeeIsNotAssignedToPractice: isEmployeeNotAssigned,
        scheduleId: this.selectedWeek.id,
        employeeNumber: employeeNumber,
      })
      .subscribe(() => {
        this.loadSchedule();
      });
  }

  timeSpanStringToDate(str: string) {
    return new Date('1900-01-01 ' + str);
  }

  timeFormat(str) {
    return format(new Date('1900-01-01 ' + str), 'h:mm a');
  }

  /*private loadSchedule(selectWeek?: number) {
    this.scheduleLoading = true;
    this.doctorScheduleClient
      .getSchedulesForPractice({
        practiceSKey: this.selectedPractice.id,
      })
      .subscribe((a) => {
        console.log(a);
        this.scheduleLoading = false;
        this.schedules = a;
        this.cloneMenuItems = this.getCloneMenuItems();
        let idx = 0;
        if (selectWeek === undefined) {
          idx = +this.selectedTab?.label;
        } else {
          idx = selectWeek;
        }
        this.tabs = a.map((s, i) => {
          return { label: i + '' };
        });
		
        this.selectWeek(idx);
        this.selectedTab = this.tabs[idx];
      });
  }*/

	private loadSchedule(selectWeek?: number) {
	  this.scheduleLoading = true;
	  this.doctorScheduleClient
		.getSchedulesForPractice({ practiceSKey: this.selectedPractice.id })
		.subscribe((a) => {
		  this.scheduleLoading = false;
		  this.schedules = a;
		  this.cloneMenuItems = this.getCloneMenuItems();

		  this.tabs = a.map((s, i) => ({
			label: `Week ${i + 1}`,
			alternating: i > 0
		  }));

		  this.activeTabIndex = selectWeek ?? 0;
		  this.selectWeek(this.activeTabIndex);
		});
	}

	public removeWeek(index: number): void {
	  const weekToDelete = this.schedules[index];

	  if (!weekToDelete) return;

	  this.doctorScheduleClient.deleteWeek(weekToDelete.id).subscribe(() => {
		this.loadSchedule(0);
	  });
	}



  public addWeek(cloneFrom?: number) {
    this.working = true;
    this.doctorScheduleClient
      .addNewWeek({
        practiceSKey: this.selectedPractice.id,
        copyFromWeek: cloneFrom ?? 0,
      })
      .subscribe(() => {
        this.loadSchedule(this.schedules.length);
        this.working = false;
      });
  }

  /*public removeWeek($event) {
    this.doctorScheduleClient.deleteWeek(this.selectedWeek.id).subscribe(() => {
      this.loadSchedule(0);
    });
  }*/

  private selectWeek(idx?: number) {
    this.rows = this.schedules[idx].doctors;
    this.selectedWeekStartDate = new Date(this.schedules[0].startDate);
    this.selectedTab = this.tabs[idx];
    this.selectedWeek = this.schedules[idx];
  }

  public onActiveItemChange($event) {
    this.selectWeek(+$event.label);
  }
  
  public onTabChanged(index: number): void {
    this.selectWeek(index);
  }

  public onStartDateChanged($event) {
    const dateString =
      format(this.selectedWeekStartDate, 'yyyy-MM-dd') + 'T00:00:00.000Z';
    const date = new Date(dateString);
    this.schedules[0].startDate = this.selectedWeekStartDate;
    this.doctorScheduleClient
      .changeWeekStartDate({
        practiceSKey: this.schedules[0].practiceSKey,
        startDate: date,
      })
      .subscribe();
  }

  public getWeekDayName(dow) {
    let firstDow = startOfWeek(new Date());
    return format(add(firstDow, { days: dow }), 'EEEE');
  }

  private loadAuth() {
    this.authManager.getCurrentUser().subscribe((u) => {
      this.currentUser = u;
      this.practices = u.practices.filter((p) => p.reportingStatusSKey != 2);
      this.doctorScheduleClient.getBlockoffTypes().subscribe((bt) => {
        this.blockoffTypes = bt;
        this.loading = false;
      });
    });
  }

  public getBlockoffTypeName(blockoffTypeSKey: number) {
    var retval = this.blockoffTypes.find(
      (bt) => bt.blockoffTypeSKey == blockoffTypeSKey
    );
    return retval.blockoffTypeName;
  }

  getMaxDate(dates: Date[]) {
    return max(dates);
  }

  getMinDate(dates: Date[]) {
    return min(dates);
  }
}
